// LearnCore Frontend - 响应式布局组件（支持侧边栏和顶部导航）

import React, { useState, useRef, useEffect } from 'react';
import '../styles/sidebar-styles.css';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../api/client';
import { useScreenOrientation } from '../hooks/useScreenOrientation';
import TopNavigation from './TopNavigation';
import {
  BookOpenIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  GlobeAltIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CheckCircleIcon,
  GiftIcon,
  CalendarIcon,
  UsersIcon,
  HomeIcon,
  DocumentTextIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

interface SidebarLayoutProps {
  children: React.ReactNode;
}

export default function SidebarLayout({ children }: SidebarLayoutProps) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const screenOrientation = useScreenOrientation();
  const [profile, setProfile] = useState<{ nickname?: string; avatar_url?: string } | null>(null);
  const [isToolsExpanded, setIsToolsExpanded] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // 根据屏幕方向决定布局模式 - 竖屏强制使用顶部导航，横屏使用侧边栏
  const useSidebarLayout = screenOrientation.isLandscape;

  // 开发环境调试信息
  if (import.meta.env.DEV) {
    console.log('🖥️ 屏幕信息:', {
      orientation: screenOrientation.orientation,
      width: screenOrientation.width,
      height: screenOrientation.height,
      isPortrait: screenOrientation.isPortrait,
      isLandscape: screenOrientation.isLandscape,
      isSmallDevice: screenOrientation.isSmallDevice,
      isTablet: screenOrientation.isTablet,
      isDesktop: screenOrientation.isDesktop,
      useSidebarLayout
    });
  }

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('登出失败:', error);
    }
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const isToolsSubPath = () => {
    return location.pathname.startsWith('/flashcard') ||
           location.pathname.startsWith('/passages') ||
           location.pathname.startsWith('/classical-chinese');
  };

  useEffect(() => {
    let isMounted = true;
    const fetchSettings = async () => {
      if (!user) {
        setProfile(null);
        return;
      }
      try {
        const resp = await apiClient.getUserSettings();
        if (!isMounted) return;
        setProfile(resp.settings.profile || null);
      } catch (e) {
        // 忽略错误，使用邮箱兜底
      }
    };
    fetchSettings();
    return () => {
      isMounted = false;
    };
  }, [user]);

  useEffect(() => {
    // 自动展开学习工具如果当前路径是其子路径
    if (isToolsSubPath()) {
      setIsToolsExpanded(true);
    }
  }, [location.pathname]);

  // 点击外部关闭菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const navigationItems = [
    {
      name: '任务',
      path: '/tasks',
      icon: CheckCircleIcon,
      badge: null
    },
    {
      name: '奖励',
      path: '/rewards',
      icon: GiftIcon,
      badge: null
    },
    {
      name: '日历',
      path: '/calendar',
      icon: CalendarIcon,
      badge: null
    },
    {
      name: '学习市场',
      path: '/marketplace',
      icon: GlobeAltIcon,
      badge: null
    }
  ];

  const toolsSubItems = [
    {
      name: '学习集',
      path: '/flashcard',
      icon: HomeIcon
    },
    {
      name: '英文阅读',
      path: '/passages',
      icon: DocumentTextIcon
    },
    {
      name: '文言阅读',
      path: '/classical-chinese',
      icon: BookOpenIcon
    }
  ];

  // 竖屏模式 - 顶部导航栏布局
  if (!useSidebarLayout) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
        {/* 顶部导航 */}
        <TopNavigation
          onMobileMenuToggle={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        />

        {/* 移动端侧边栏（小屏幕时作为滑动菜单） */}
        {isMobileSidebarOpen && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setIsMobileSidebarOpen(false)}
            />
            <MobileSidebar
              isOpen={isMobileSidebarOpen}
              onClose={() => setIsMobileSidebarOpen(false)}
              user={user}
              profile={profile}
              location={location}
              isToolsExpanded={isToolsExpanded}
              setIsToolsExpanded={setIsToolsExpanded}
              isUserMenuOpen={isUserMenuOpen}
              setIsUserMenuOpen={setIsUserMenuOpen}
              handleLogout={handleLogout}
            />
          </>
        )}

        {/* 主内容区域 */}
        <main className="flex-1">
          {children}
        </main>

        {/* 页脚 */}
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 py-4">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <BookOpenIcon className="h-4 w-4" />
                <span>© 2025 LearnCore</span>
              </div>
              <span>版本 0.1.0</span>
            </div>
          </div>
        </footer>
      </div>
    );
  }

  // 横屏模式 - 左侧导航栏布局
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
      {/* 移动端遮罩 */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* 侧边栏 */}
      <aside className={`
        sidebar fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ease-in-out
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo区域 */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            className="flex items-center space-x-2 text-xl font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            onClick={() => setIsMobileSidebarOpen(false)}
          >
            <BookOpenIcon className="h-8 w-8 logo-icon" />
            <span className="logo-text">LearnCore</span>
          </Link>

          {/* 移动端关闭按钮 */}
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            className="lg:hidden p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* 主导航区域 */}
        <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
          {/* 主要导航项 */}
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMobileSidebarOpen(false)}
                className={`
                  nav-item flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group
                  ${isActivePath(item.path)
                    ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                  }
                `}
              >
                <div className="flex items-center space-x-3">
                  <Icon className={`h-5 w-5 icon ${
                    isActivePath(item.path) ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-500'
                  }`} />
                  <span>{item.name}</span>
                </div>
                {item.badge && (
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-600 rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}

          {/* 学习工具 - 可展开 */}
          <div className="space-y-1">
            <button
              onClick={() => setIsToolsExpanded(!isToolsExpanded)}
              className={`
                nav-item w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group
                ${isToolsSubPath()
                  ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }
              `}
            >
              <div className="flex items-center space-x-3">
                <BookOpenIcon className={`h-5 w-5 icon ${
                  isToolsSubPath() ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-500'
                }`} />
                <span>学习工具</span>
              </div>
              <ChevronRightIcon className={`h-4 w-4 tools-expand-icon transition-transform duration-200 ${
                isToolsExpanded ? 'rotate-90' : ''
              }`} />
            </button>

            {/* 学习工具子菜单 */}
            {isToolsExpanded && (
              <div className="ml-4 space-y-1">
                {toolsSubItems.map((subItem) => {
                  const Icon = subItem.icon;
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      onClick={() => setIsMobileSidebarOpen(false)}
                      className={`
                        nav-sub-item flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-all duration-200
                        ${isActivePath(subItem.path)
                          ? 'bg-primary-50 text-primary-600 border-l-2 border-primary-400'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                        }
                      `}
                    >
                      <Icon className={`h-4 w-4 icon ${
                        isActivePath(subItem.path) ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500'
                      }`} />
                      <span>{subItem.name}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {/* 家长管理入口 */}
          {user?.user_type !== 'child' && (
            <Link
              to="/parent"
              onClick={() => setIsMobileSidebarOpen(false)}
              className={`
                nav-item flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group
                ${isActivePath('/parent')
                  ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }
              `}
            >
              <div className="flex items-center space-x-3">
                <UsersIcon className={`h-5 w-5 ${
                  isActivePath('/parent') ? 'text-primary-600' : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-500'
                }`} />
                <span>家长管理</span>
              </div>
            </Link>
          )}
        </nav>

        {/* 用户信息区域 */}
        {user && (
          <div className="user-info border-t border-gray-200 p-4">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className="user-menu-button w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="avatar" className="user-avatar h-8 w-8 rounded-full object-cover border" />
                ) : (
                  <UserCircleIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                )}
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {profile?.nickname || user.email}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.user_type === 'child' ? '孩子账户' : '家长账户'}
                  </p>
                </div>
                <ChevronDownIcon className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform ${
                  isUserMenuOpen ? 'rotate-180' : ''
                }`} />
              </button>

              {/* 用户下拉菜单 */}
              {isUserMenuOpen && (
                <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  <div className="py-1">
                    <Link
                      to="/study-time"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                        isActivePath('/study-time')
                          ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <ClockIcon className="h-4 w-4" />
                      <span>学习时间</span>
                    </Link>

                    <Link
                      to="/user/center"
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                        isActivePath('/user/center')
                          ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                          : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                      }`}
                    >
                      <Cog6ToothIcon className="h-4 w-4" />
                      <span>设置</span>
                    </Link>

                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        handleLogout();
                      }}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4" />
                      <span>登出</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* 主内容区域 */}
      <div className="flex-1 flex flex-col min-h-screen bg-gray-50 dark:bg-gray-900">
        {/* 移动端顶部导航栏 */}
        <header className="mobile-header lg:hidden bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex items-center space-x-2 text-lg font-semibold text-primary-600 dark:text-primary-400">
              <BookOpenIcon className="h-6 w-6" />
              <span>LearnCore</span>
            </div>

            {user && (
              <div className="flex items-center space-x-2">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="avatar" className="user-avatar h-8 w-8 rounded-full object-cover border" />
                ) : (
                  <UserCircleIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                )}
              </div>
            )}
          </div>
        </header>

        {/* 主内容 */}
        <main className="main-content flex-1 bg-gray-50 dark:bg-gray-900">
          {children}
        </main>

        {/* 页脚 */}
        <footer className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
          <div className="px-4 py-4">
            <div className="flex justify-between items-center text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center space-x-2">
                <BookOpenIcon className="h-4 w-4" />
                <span>© 2025 LearnCore</span>
              </div>
              <span>版本 0.1.0</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

// 移动端侧边栏组件
interface MobileSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  profile: { nickname?: string; avatar_url?: string } | null;
  location: any;
  isToolsExpanded: boolean;
  setIsToolsExpanded: (expanded: boolean) => void;
  isUserMenuOpen: boolean;
  setIsUserMenuOpen: (open: boolean) => void;
  handleLogout: () => void;
}

function MobileSidebar({
  isOpen,
  onClose,
  user,
  profile,
  location,
  isToolsExpanded,
  setIsToolsExpanded,
  isUserMenuOpen,
  setIsUserMenuOpen,
  handleLogout
}: MobileSidebarProps) {
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  const isToolsSubPath = () => {
    return location.pathname.startsWith('/flashcard') ||
           location.pathname.startsWith('/passages') ||
           location.pathname.startsWith('/classical-chinese');
  };

  const navigationItems = [
    { name: '任务', path: '/tasks', icon: CheckCircleIcon },
    { name: '奖励', path: '/rewards', icon: GiftIcon },
    { name: '日历', path: '/calendar', icon: CalendarIcon },
    { name: '学习市场', path: '/marketplace', icon: GlobeAltIcon }
  ];

  const toolsSubItems = [
    { name: '学习集', path: '/dashboard', icon: HomeIcon },
    { name: '英文阅读', path: '/passages', icon: DocumentTextIcon },
    { name: '文言阅读', path: '/classical-chinese', icon: BookOpenIcon }
  ];

  return (
    <aside className={`
      mobile-sidebar fixed inset-y-0 left-0 z-50 w-72 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-200 ease-in-out
      ${isOpen ? 'translate-x-0' : '-translate-x-full'}
    `}>
      {/* Logo区域 */}
      <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
        <Link
          to="/"
          className="flex items-center space-x-2 text-xl font-bold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          onClick={onClose}
        >
          <BookOpenIcon className="h-8 w-8" />
          <span>LearnCore</span>
        </Link>

        <button
          onClick={onClose}
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* 导航区域 */}
      <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
        {navigationItems.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={onClose}
              className={`
                nav-item flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors
                ${isActivePath(item.path)
                  ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600 dark:bg-primary-900/20 dark:text-primary-400 dark:border-primary-400'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                }
              `}
            >
              <Icon className={`h-5 w-5 ${
                isActivePath(item.path) ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
              }`} />
              <span>{item.name}</span>
            </Link>
          );
        })}

        {/* 学习工具 */}
        <div className="space-y-1">
          <button
            onClick={() => setIsToolsExpanded(!isToolsExpanded)}
            className={`
              nav-item w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm font-medium transition-colors
              ${isToolsSubPath()
                ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
              }
            `}
          >
            <div className="flex items-center space-x-3">
              <BookOpenIcon className={`h-5 w-5 ${
                isToolsSubPath() ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500'
              }`} />
              <span>学习工具</span>
            </div>
            <ChevronRightIcon className={`h-4 w-4 transition-transform ${
              isToolsExpanded ? 'rotate-90' : ''
            }`} />
          </button>

          {isToolsExpanded && (
            <div className="ml-4 space-y-1">
              {toolsSubItems.map((subItem) => {
                const Icon = subItem.icon;
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    onClick={onClose}
                    className={`
                      nav-sub-item flex items-center space-x-3 px-3 py-2 rounded-lg text-sm transition-colors
                      ${isActivePath(subItem.path)
                        ? 'bg-primary-50 text-primary-600 border-l-2 border-primary-400'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100'
                      }
                    `}
                  >
                    <Icon className={`h-4 w-4 ${
                      isActivePath(subItem.path) ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500'
                    }`} />
                    <span>{subItem.name}</span>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* 家长管理 */}
        {user?.user_type !== 'child' && (
          <Link
            to="/parent"
            onClick={onClose}
            className={`
              flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium transition-colors
              ${isActivePath('/parent')
                ? 'bg-primary-50 text-primary-600 border-l-4 border-primary-600'
                : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100'
              }
            `}
          >
            <UsersIcon className={`h-5 w-5 ${
              isActivePath('/parent') ? 'text-primary-600' : 'text-gray-400 dark:text-gray-500'
            }`} />
            <span>家长管理</span>
          </Link>
        )}
      </nav>

      {/* 用户信息区域 */}
      {user && (
        <div className="border-t border-gray-200 dark:border-gray-700 p-4">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
              className="w-full flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors dark:hover:bg-gray-700"
            >
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} alt="avatar" className="h-8 w-8 rounded-full object-cover border" />
              ) : (
                <UserCircleIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
              )}
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                  {profile?.nickname || user.email}
                </p>
                <p className="text-xs text-gray-500 truncate">
                  {user.user_type === 'child' ? '孩子账户' : '家长账户'}
                </p>
              </div>
              <ChevronDownIcon className={`h-4 w-4 text-gray-400 dark:text-gray-500 transition-transform ${
                isUserMenuOpen ? 'rotate-180' : ''
              }`} />
            </button>

            {isUserMenuOpen && (
              <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <div className="py-1">
                  <Link
                    to="/study-time"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      onClose();
                    }}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                      isActivePath('/study-time')
                        ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    <ClockIcon className="h-4 w-4" />
                    <span>学习时间</span>
                  </Link>

                  <Link
                    to="/user/center"
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      onClose();
                    }}
                    className={`flex items-center space-x-2 px-4 py-2 text-sm transition-colors ${
                      isActivePath('/user/center')
                        ? 'text-primary-600 bg-primary-50 dark:text-primary-400 dark:bg-primary-900/20'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    <Cog6ToothIcon className="h-4 w-4" />
                    <span>设置</span>
                  </Link>

                  <button
                    onClick={() => {
                      setIsUserMenuOpen(false);
                      handleLogout();
                    }}
                    className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:text-red-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700 transition-colors"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    <span>登出</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}